// components/GolferSidebar.js
// Desktop sidebar navigation for golfer-facing pages.
// Mirrors the ClubDashboard sidebar style. Hidden on mobile (<769px).

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './GolferSidebar.css';

// SVG Icons (matching ClubDashboard pattern)
const Icons = {
  Home: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  Feed: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="17" y1="10" x2="3" y2="10" />
      <line x1="21" y1="6" x2="3" y2="6" />
      <line x1="21" y1="14" x2="3" y2="14" />
      <line x1="17" y1="18" x2="3" y2="18" />
    </svg>
  ),
  Map: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
      <line x1="8" y1="2" x2="8" y2="18" />
      <line x1="16" y1="6" x2="16" y2="22" />
    </svg>
  ),
  Forums: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  ),
  Chat: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  User: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Dashboard: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  ),
  ExternalLink: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  ),
  LogOut: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  ),
};

const GolferSidebar = () => {
  const location = useLocation();
  const [isClubUser, setIsClubUser] = useState(false);

  // Check if user is a club user (same pattern as MobileNavbar & Navbar)
  useEffect(() => {
    const checkClubStatus = () => {
      try {
        const userProfile = localStorage.getItem('user_profile');
        if (userProfile) {
          const profile = JSON.parse(userProfile);
          setIsClubUser(profile.is_club === true);
        }
      } catch (e) {
        setIsClubUser(false);
      }
    };
    checkClubStatus();
  }, [location.pathname]);

  // Navigation items
  const navItems = [
    { to: '/feestable', label: 'Home', icon: <Icons.Home /> },
    { to: '/feed', label: 'Feed', icon: <Icons.Feed /> },
    { to: '/courses', label: 'Map', icon: <Icons.Map /> },
    { to: '/forums', label: 'Forums', icon: <Icons.Forums /> },
    { to: '/chat', label: 'Chat', icon: <Icons.Chat /> },
  ];

  // Check if a route is active (exact match or starts-with for nested routes)
  const isActive = (path) => {
    if (path === '/feestable') {
      return location.pathname === '/feestable' || location.pathname === '/';
    }
    if (path === '/forums') {
      return location.pathname.startsWith('/forums');
    }
    return location.pathname === path;
  };

  return (
    <aside className="golfer-sidebar">
      {/* Branding — visible when navbar scrolls away */}
      <div className="gs-brand-strip">
        <span className="gs-brand-est">Est. 2024</span>
        <span className="gs-brand-divider">|</span>
        <span className="gs-brand-loc">Dallas, TX</span>
      </div>

      {/* Navigation */}
      <nav className="gs-nav">
        {/* Main nav section */}
        <div className="gs-nav-section">
          <span className="gs-nav-label">Navigate</span>
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`gs-nav-item ${isActive(item.to) ? 'active' : ''}`}
            >
              <span className="gs-nav-icon">{item.icon}</span>
              <span>{item.label}</span>
            </Link>
          ))}

          {/* My IQ - hidden for club users (matches MobileNavbar behavior) */}
          {!isClubUser && (
            <Link
              to="/myiq"
              className={`gs-nav-item ${isActive('/myiq') ? 'active' : ''}`}
            >
              <span className="gs-nav-icon"><Icons.User /></span>
              <span>My IQ</span>
            </Link>
          )}
        </div>

        {/* Club Dashboard link for club users */}
        {isClubUser && (
          <div className="gs-nav-section">
            <span className="gs-nav-label">Club Admin</span>
            <Link
              to="/myclub/dashboard"
              className="gs-nav-item gs-dashboard-link"
            >
              <span className="gs-nav-icon"><Icons.Dashboard /></span>
              <span>Dashboard</span>
              <span className="gs-external-icon"><Icons.ExternalLink /></span>
            </Link>
          </div>
        )}
      </nav>

      {/* Footer */}
      <div className="gs-footer">
        <Link to="/logout" className="gs-logout-btn">
          <Icons.LogOut />
          <span>Log Out</span>
        </Link>
        <div className="gs-contact">
          <a href="mailto:support@privateiq.golf" className="gs-help-link">
            Need help?
          </a>
        </div>
      </div>
    </aside>
  );
};

export default GolferSidebar;
